// SearchableSelectField.tsx
import React, { useMemo, useCallback, useEffect } from "react";
import { Controller, useFormContext, useWatch, type Control, type FieldValues } from "react-hook-form";
import Select, { type SingleValue, type MultiValue, type ActionMeta } from "react-select";
import MyBox from "@/MY_COMPONENTS/MyBox";
import { type FormField } from "../formFieldType";

export interface CustomOption {
  value: string | number | boolean;
  label: string;
  icon?: string;
  flag?: React.ReactNode;
  name?: string;
  showBox?: boolean;
}

export type OptionValue = string | number | boolean | null;
export type SelectedValue = OptionValue | OptionValue[];

export interface SearchableSelectFieldProps {
  field: FormField & {
    dependsOn?: string;
    optionsMap?: Record<string, any[] | (() => any[])>;
    onSelect?: (option: CustomOption | CustomOption[] | null) => void;
    loading?: boolean;
    rightButton?: React.ReactNode;
    visibleWhen?: { field: string; notEmpty?: boolean; value?: any };
    showBox?: boolean;
    clearOnDependencyChange?: boolean;
  };
}

export function SearchableSelectField({ field }: SearchableSelectFieldProps) {
  const formContext = useFormContext();
  
  // Handle null context safely
  if (!formContext) {
    throw new Error(
      "SearchableSelectField must be used within a FormProvider or form context. " +
      "Please wrap your form with <FormProvider {...methods}>"
    );
  }

  const { control, setValue, getValues, trigger } = formContext;

  // Watch parent field value
  const parentValue = field.dependsOn
    ? useWatch({ name: field.dependsOn, control })
    : undefined;

  // Watch current field value
  const currentValue = useWatch({ name: field.name, control });

  // Check visibility
  const isVisible = useMemo(() => {
    if (!field.visibleWhen) return true;
    
    const dependentValue = getValues(field.visibleWhen.field);
    
    if (field.visibleWhen.notEmpty) {
      return dependentValue && dependentValue.length > 0;
    }
    
    if (field.visibleWhen.value !== undefined) {
      return dependentValue === field.visibleWhen.value;
    }
    
    return true;
  }, [field.visibleWhen, getValues]);

  // Resolve options based on dependency
  const options = useMemo(() => {
    try {
      if (field.dependsOn && field.optionsMap) {
        const dependentOptions = field.optionsMap[parentValue];
        if (typeof dependentOptions === 'function') {
          const result = dependentOptions();
          return Array.isArray(result) ? result : [];
        }
        return Array.isArray(dependentOptions) ? dependentOptions : [];
      }
      
      if (typeof field.options === 'function') {
        const result = field.options();
        if (result instanceof Promise) {
          // Handle async options - you might want to add state for this
          return [];
        }
        return Array.isArray(result) ? result : [];
      }
      
      return Array.isArray(field.options) ? field.options : [];
    } catch (error) {
      console.error("Error resolving options:", error);
      return [];
    }
  }, [parentValue, field.options, field.optionsMap, field.dependsOn]);

  // Normalize options to CustomOption format
  const normalizedOptions: CustomOption[] = useMemo(() => {
    if (!Array.isArray(options)) return [];
    
    return options.map((opt): CustomOption => {
      if (typeof opt === "string") {
        return { label: opt, value: opt };
      }
      if (opt && typeof opt === "object" && "value" in opt && "label" in opt) {
        return opt as CustomOption;
      }
      return { label: String(opt), value: String(opt) };
    });
  }, [options]);

  // Reset current field value when parent changes
  const shouldClearOnDependencyChange = field.clearOnDependencyChange ?? true;
  
  useEffect(() => {
    if (!field.dependsOn) return;
    if (parentValue === undefined) return;
    if (!shouldClearOnDependencyChange) return;
    
    const currentFormValue = getValues(field.name);
    
    const hasValue = currentFormValue && 
      currentFormValue !== "" && 
      (!Array.isArray(currentFormValue) || currentFormValue.length > 0);
    
    if (hasValue) {
      const newValue = field.isMulti ? [] : "";
      setValue(field.name, newValue, { 
        shouldDirty: true, 
        shouldValidate: true 
      });
      
      if (field.onSelect) {
        field.onSelect(null);
      }
    }
  }, [parentValue, field.dependsOn, field.name, setValue, getValues, field.onSelect, field.isMulti, shouldClearOnDependencyChange]);

  // Validate when options change
  useEffect(() => {
    if (!currentValue) return;
    if (normalizedOptions.length === 0) return;
    
    if (field.isMulti && Array.isArray(currentValue)) {
      const isValid = currentValue.every(val => 
        normalizedOptions.some(opt => opt.value === val)
      );
      if (!isValid) {
        setValue(field.name, [], { shouldDirty: true, shouldValidate: true });
        trigger(field.name);
      }
    } else if (!field.isMulti && currentValue !== null && currentValue !== "") {
      const isValid = normalizedOptions.some(opt => opt.value === currentValue);
      if (!isValid) {
        setValue(field.name, "", { shouldDirty: true, shouldValidate: true });
        trigger(field.name);
      }
    }
  }, [normalizedOptions, currentValue, field.name, setValue, trigger, field.isMulti]);

  // Check if field should be disabled
  const isDisabled = useMemo(() => {
    return field.disabled || (field.dependsOn && !parentValue && field.required);
  }, [field.disabled, field.dependsOn, parentValue, field.required]);

  // Get selected value for react-select
  const selectedValue = useMemo(() => {
    if (field.isMulti) {
      if (Array.isArray(currentValue) && currentValue.length > 0) {
        return normalizedOptions.filter(opt => currentValue.includes(opt.value));
      }
      return [];
    } else {
      const found = normalizedOptions.find(opt => opt.value === currentValue);
      return found || null;
    }
  }, [currentValue, normalizedOptions, field.isMulti]);

  // Determine placeholder text
  const getPlaceholder = useCallback(() => {
    if (field.disabled) return "This field is disabled";
    if (field.dependsOn && !parentValue) return "Select parent field first";
    if (field.placeholder) return field.placeholder;
    if (field.isMulti) return "Search and select options...";
    return "Search or select an option...";
  }, [field.disabled, field.dependsOn, parentValue, field.placeholder, field.isMulti]);

  // Handle selection change
  const handleSelectChange = useCallback(
    (opt: SingleValue<CustomOption> | MultiValue<CustomOption>, actionMeta: ActionMeta<CustomOption>) => {
      let newValue: SelectedValue;
      let selectedOption: CustomOption | CustomOption[] | null = null;
      
      if (field.isMulti) {
        const multiValue = opt as MultiValue<CustomOption>;
        newValue = multiValue.map(o => o.value);
        selectedOption = multiValue;
      } else {
        // Handle clear action
        if (actionMeta.action === 'clear') {
          newValue = "";
          selectedOption = null;
        } else {
          const singleValue = opt as SingleValue<CustomOption>;
          newValue = singleValue?.value ?? "";
          selectedOption = singleValue || null;
        }
      }
      
      // Update form
      setValue(field.name, newValue, { 
        shouldDirty: true, 
        shouldValidate: true 
      });
      
      // Call onSelect callback
      if (field.onSelect && selectedOption) {
        field.onSelect(selectedOption);
      }
      
      // Trigger validation
      trigger(field.name);
    },
    [field, setValue, trigger]
  );

  // Don't render if not visible
  if (!isVisible) return null;

  // Loading state
  const isLoading = field.loading || (normalizedOptions.length === 0 && field.dependsOn && parentValue);
  
  if (isLoading) {
    return (
      <MyBox topText={field.label} required={field.required} topInfo={field.topInfo} bottomInfo={field.bottomInfo}>
        <div className="relative z-20 flex w-full items-end gap-2">
          <div className="flex-1">
            <div className="h-[38px] w-full animate-pulse bg-gray-200 dark:bg-gray-700 rounded-md" />
          </div>
          {field.rightButton && (
            <div className="h-[38px] flex items-center">{field.rightButton}</div>
          )}
        </div>
      </MyBox>
    );
  }

  // No options state
  if (normalizedOptions.length === 0 && !field.dependsOn) {
    return (
      <MyBox topText={field.label} required={field.required} topInfo={field.topInfo} bottomInfo={field.bottomInfo}>
        <div className="relative z-20 flex w-full items-end gap-2">
          <div className="flex-1">
            <div className="h-[38px] w-full bg-gray-100 dark:bg-gray-800 rounded-md flex items-center px-3 text-sm text-gray-500">
              No options available
            </div>
          </div>
          {field.rightButton && (
            <div className="h-[38px] flex items-center">{field.rightButton}</div>
          )}
        </div>
      </MyBox>
    );
  }

  // Determine if MyBox should be used (defaults to true)
  const shouldShowBox = field.showBox ?? true;

  // Render content
  const renderContent = (hasError: boolean = false, errorMessage?: string) => (
    <>
      <div className="relative z-20 flex w-full items-end gap-2">
        <div className="flex-1">
          <Select
            isMulti={field.isMulti}
            options={normalizedOptions}
            value={selectedValue}
            onChange={handleSelectChange}
            isDisabled={isDisabled}
            isClearable={field.clearable !== false}
            placeholder={getPlaceholder()}
            classNamePrefix="react-select"
            menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
            menuPosition="fixed"
            isSearchable={true}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 999999 }),
              menu: (base) => ({
                ...base,
                zIndex: 999999,
                backgroundColor: "var(--select-bg, #ffffff)",
              }),
              control: (base, state) => ({
                ...base,
                backgroundColor: "var(--select-bg, #ffffff)",
                borderColor: hasError ? "#ef4444" : state.isFocused ? "#3b82f6" : "var(--select-border, #d1d5db)",
                boxShadow: state.isFocused ? "0 0 0 1px #3b82f6" : "none",
                "&:hover": {
                  borderColor: hasError ? "#ef4444" : "#3b82f6",
                },
              }),
              singleValue: (base) => ({
                ...base,
                color: "var(--select-text, #1f2937)",
              }),
              input: (base) => ({
                ...base,
                color: "var(--select-text, #1f2937)",
              }),
              placeholder: (base) => ({
                ...base,
                color: "var(--select-placeholder, #9ca3af)",
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected
                  ? "#3b82f6"
                  : state.isFocused
                    ? "var(--select-hover, #f3f4f6)"
                    : "transparent",
                color: state.isSelected ? "#ffffff" : "var(--select-text, #1f2937)",
                cursor: "pointer",
              }),
              multiValue: (base) => ({
                ...base,
                backgroundColor: "var(--select-chip, #e5e7eb)",
              }),
              multiValueLabel: (base) => ({
                ...base,
                color: "var(--select-text, #1f2937)",
              }),
              multiValueRemove: (base) => ({
                ...base,
                color: "var(--select-text, #1f2937)",
                ":hover": {
                  backgroundColor: "#ef4444",
                  color: "#ffffff",
                },
              }),
            }}
            formatOptionLabel={({ label, name, flag, icon, value }) => (
              <div className="flex items-center gap-2">
                {flag && <span className="text-lg">{flag}</span>}
                {icon && (
                  <img
                    src={icon}
                    alt=""
                    className="h-5 w-5 rounded-sm object-contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                )}
                <span>{label ?? name ?? String(value)}</span>
              </div>
            )}
            theme={(theme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary: "#3b82f6",
                primary25: "var(--select-hover, #f3f4f6)",
              },
            })}
          />
          {hasError && errorMessage && (
            <p className="mt-1 text-sm text-red-500">{errorMessage}</p>
          )}
          {field.helperText && !hasError && (
            <p className="mt-1 text-sm text-gray-500">{field.helperText}</p>
          )}
        </div>
        
        {field.rightButton && (
          <div className="h-[38px] flex items-center">{field.rightButton}</div>
        )}
      </div>
    </>
  );

  // Validation rules
  const validationRules = useMemo(() => {
    const rules: {
      required?: string | boolean;
      validate?: (value: any) => string | boolean;
    } = {};
    
    if (field.required) {
      rules.required = `${field.label} is required`;
      rules.validate = (value: any) => {
        if (!value) return `${field.label} is required`;
        if (field.isMulti && Array.isArray(value) && value.length === 0) {
          return `${field.label} is required`;
        }
        return true;
      };
    }
    
    return rules;
  }, [field.required, field.label, field.isMulti]);

  // Conditional rendering based on showBox prop
  if (shouldShowBox) {
    return (
      <Controller
        name={field.name}
        control={control}
        rules={validationRules}
        render={({ fieldState }) => (
          <MyBox 
            topInfo={field.topInfo} 
            bottomInfo={field.bottomInfo} 
            topText={field.label} 
            required={field.required}
          >
            {renderContent(!!fieldState.error, fieldState.error?.message)}
          </MyBox>
        )}
      />
    );
  }

  // Render without MyBox wrapper
  return (
    <Controller
      name={field.name}
      control={control}
      rules={validationRules}
      render={({ fieldState }) => renderContent(!!fieldState.error, fieldState.error?.message)}
    />
  );
}

export default SearchableSelectField;